/* Created by Interactive 63 - https://interactive63.com */
/**
 * The artboard controls shown when nothing is selected: the frame's size and position, which
 * part of it stays put when it is resized, size presets, fitting it to the artwork, and the
 * colour behind the artwork. The artwork never moves, so none of these touch an element.
 */
import {
  ARTBOARD_MAX_SIDE,
  ARTBOARD_MIN_SIDE,
  ARTBOARD_PRESETS,
  boundsToArtboard,
  resizeAround,
  type Anchor,
  type Box,
} from '../../../core/model/artboard';
import { VIDEO_FALLBACK_BACKGROUND } from '../../../core/model/media';
import { setArtboard, setArtboardBackground } from '../../store/artboard-actions';
import type { Store } from '../../store/store';
import { h } from '../../ui/dom';
import { toast } from '../../ui/toast';
import { NumberInput } from './inputs/number-input';

export interface ArtboardTools {
  /** The box around all the artwork, in the artboard's units, or null when it cannot be read. */
  artworkBounds(): Box | null;
}

const STEPS = [0, 0.5, 1];
const ROW_NAMES = ['top', 'middle', 'bottom'];
const COLUMN_NAMES = ['left', 'centre', 'right'];
const POSITION_RANGE = 100000;

export class ArtboardSection {
  readonly el: HTMLElement;
  private anchor: Anchor = { x: 0.5, y: 0.5 };
  private readonly width: NumberInput;
  private readonly height: NumberInput;
  private readonly left: NumberInput;
  private readonly top: NumberInput;
  private readonly cells: HTMLButtonElement[] = [];
  private readonly mode: HTMLSelectElement;
  private readonly colour: HTMLInputElement;

  /** Builds the section. */
  constructor(
    private readonly store: Store,
    private readonly tools: ArtboardTools,
  ) {
    const side = (label: string, change: (value: number, key?: string) => void): NumberInput =>
      new NumberInput({
        label,
        unit: 'px',
        min: ARTBOARD_MIN_SIDE,
        max: ARTBOARD_MAX_SIDE,
        step: 1,
        onChange: change,
        scrubKey: `scrub:artboard:${label}`,
      });
    this.width = side('Width', (value, key) => {
      const box = this.box();
      this.write(resizeAround(box, value, box.height, this.anchor), key);
    });
    this.height = side('Height', (value, key) => {
      const box = this.box();
      this.write(resizeAround(box, box.width, value, this.anchor), key);
    });
    const position = (label: string, axis: 'x' | 'y'): NumberInput =>
      new NumberInput({
        label,
        min: -POSITION_RANGE,
        max: POSITION_RANGE,
        step: 1,
        onChange: (value, key) => {
          this.write({ ...this.box(), [axis]: value }, key);
        },
        scrubKey: `scrub:artboard:${label}`,
      });
    this.left = position('Left', 'x');
    this.top = position('Top', 'y');

    const grid = h('div', {
      class: 'origin-grid',
      role: 'group',
      'aria-label': 'Part of the artboard that stays put when it is resized',
    });
    STEPS.forEach((y, row) => {
      STEPS.forEach((x, column) => {
        const title = `Keep the ${ROW_NAMES[row]} ${COLUMN_NAMES[column]} fixed`;
        const cell = h('button', {
          type: 'button',
          class: 'origin-cell',
          'aria-label': title,
          title,
          'data-x': x,
          'data-y': y,
        });
        cell.addEventListener('click', () => {
          this.anchor = { x, y };
          this.refresh();
        });
        this.cells.push(cell);
        grid.append(cell);
      });
    });

    const preset = h(
      'select',
      { 'aria-label': 'Artboard size preset' },
      h('option', { value: '' }, 'Preset size…'),
      ...ARTBOARD_PRESETS.map((p, index) => h('option', { value: index }, p.label)),
    );
    preset.addEventListener('change', () => {
      const chosen = ARTBOARD_PRESETS[Number(preset.value)];
      preset.value = '';
      if (chosen) this.write(resizeAround(this.box(), chosen.width, chosen.height, this.anchor));
    });

    const fit = h(
      'button',
      { type: 'button', title: 'Resize the artboard to the smallest box that holds the artwork' },
      'Fit to artwork',
    );
    fit.addEventListener('click', () => {
      const bounds = this.tools.artworkBounds();
      if (!bounds) {
        toast.info('There is no artwork to fit to yet.');
        return;
      }
      this.write(boundsToArtboard(bounds));
    });

    this.mode = h(
      'select',
      { 'aria-label': 'Artboard background' },
      h('option', { value: 'clear' }, 'Transparent'),
      h('option', { value: 'solid' }, 'Solid colour'),
    );
    this.colour = h('input', {
      type: 'color',
      value: VIDEO_FALLBACK_BACKGROUND,
      'aria-label': 'Artboard background colour',
    });
    this.mode.addEventListener('change', () => {
      setArtboardBackground(this.store, this.mode.value === 'clear' ? null : this.colour.value);
    });
    this.colour.addEventListener('input', () => {
      setArtboardBackground(this.store, this.colour.value, 'artboard:colour');
    });

    this.el = h(
      'section',
      { class: 'inspector-section artboard-section' },
      h('h3', { class: 'section-title' }, 'Artboard'),
      h('div', { class: 'artboard-fields' }, this.width.el, this.height.el),
      h('div', { class: 'artboard-fields' }, this.left.el, this.top.el),
      h(
        'div',
        { class: 'origin-section' },
        h('div', { class: 'origin-title' }, 'Resize from'),
        h('div', { class: 'origin-controls' }, grid, preset),
      ),
      h('div', { class: 'artboard-actions' }, fit),
      h(
        'label',
        { class: 'keyframe-field' },
        'Background',
        h('span', { class: 'settings-inline' }, this.mode, this.colour),
      ),
      h(
        'p',
        { class: 'panel-empty' },
        'Drag the handles on the stage to resize it. The artwork stays where it is. The background shows in the editor, GIF and MP4 only.',
      ),
    );
    this.refresh();
  }

  /** The artboard as it is now. */
  private box(): Box {
    return this.store.getState().project.svg.viewBox;
  }

  /** Writes a new artboard frame. */
  private write(box: Box, key?: string): void {
    const now = this.box();
    if (
      box.x === now.x &&
      box.y === now.y &&
      box.width === now.width &&
      box.height === now.height
    ) {
      return;
    }
    setArtboard(this.store, box, key);
  }

  /** Updates the fields from the store, leaving a field alone while it is being edited. */
  refresh(): void {
    const { viewBox, background } = this.store.getState().project.svg;
    this.width.setValue(viewBox.width);
    this.height.setValue(viewBox.height);
    this.left.setValue(viewBox.x);
    this.top.setValue(viewBox.y);
    for (const cell of this.cells) {
      const active =
        Number(cell.dataset['x']) === this.anchor.x && Number(cell.dataset['y']) === this.anchor.y;
      cell.classList.toggle('is-active', active);
      cell.setAttribute('aria-pressed', String(active));
    }
    this.mode.value = background === undefined ? 'clear' : 'solid';
    if (background !== undefined && document.activeElement !== this.colour) {
      this.colour.value = background;
    }
    this.colour.hidden = background === undefined;
  }
}
